import formatISO from "date-fns/formatISO"
import React, { useEffect } from "react"
import { DependentInsert, DependentRelationship, DependentRow, isDeleted } from "../../../types/supabase"
import { useAppDispatch, useAppSelector } from "../../hooks/reduxToolkit"
import { useAsyncAction } from "../../hooks/useAsyncAction"
import { selectUsersAndDependentsForMembership } from "../../../lib/queries/selectUsersAndDependentsForMembership"
import { useClient } from "../../providers/supabase"
import { useBackendApi } from "../../hooks/useBackendApi"
import { removeDependent as removeDependentCommand, saveDependent } from "@healthshare/shared/commands/account"
import { onSyncDependents } from "../../reduxToolkit/dependentsSlice"
import { isFamilyPlanRequiredError, useInviteSpouse } from "../../hooks/useInviteSpouse"
import { useSubscriptionPrompt } from "../../hooks/useSubscriptionPrompt"
import { Tooltip } from "../tooltip"
import { DropdownSelect } from "../dropdownSelect"

import './dependentsForm.scss'


interface DependentsFormProps {

}

const now = new Date()

// eslint-disable-next-line no-empty-pattern
export function DependentsForm({
}: DependentsFormProps) {
  /* Queries */
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const {dependents, profiles} = useAppSelector((s) => s.dependents?.lastSync || {})
  const dispatch = useAppDispatch()
  const client = useClient()
  const api = useBackendApi()

  const [selectDependentsState, selectDependents] = useAsyncAction(async () => {
    const result = await selectUsersAndDependentsForMembership(client, { membershipId })
    dispatch(onSyncDependents({
      ...result
    }))

  }, [client, membershipId])
  if (selectDependentsState.error) { throw selectDependentsState.error }

  // perform an initial sync
  useEffect(() => {
    selectDependents()
  }, [selectDependents])

  /* Local state and actions */
  const [removing, setRemoving] = React.useState<string[]>([])
  const [toAdd, setToAdd] = React.useState<Partial<DependentRow>>({
    relationship: 'child'
  })
  const [removeDependentState, removeDependent] = useAsyncAction(async (id: string) => {
    setRemoving((r) => [...r, id])
    const dependent = dependents.find((d) => d.id === id)
    try {
      await removeDependentCommand(api, id)

      // Pop the just-deleted dependent back into the "adding" field
      if (dependent) {
        setToAdd(dependent)
      }
      // reload dependents from server
      await selectDependents()
    } finally {
      // splice the dependent out of the "removing" list
      setRemoving((r) =>
        r.filter((i) => i !== id))
    }
  }, [api, dependents])
  if (removeDependentState.error) { throw removeDependentState.error }

  /* Spouse invite */
  const [inviteState, invite] = useInviteSpouse()
  // Inviting a spouse requires the Family plan for Solo subscribers. The
  // no-op custom trigger keeps users without a subscription un-gated (free
  // trial behavior unchanged) — only the Solo-tier family check fires.
  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'custom',
    shouldPrompt: () => false,
    requiresFamilyPlan: () => true,
    upgradeTitle: 'Family Plan Required',
    upgradeMessage: 'Upgrade to the Family plan to invite your spouse as a co-administrator of your membership.',
  })
  // The id of the spouse dependent currently being invited (drives the inline
  // email panel), plus the email being entered for them.
  const [invitingId, setInvitingId] = React.useState<string | null>(null)
  const [inviteEmail, setInviteEmail] = React.useState('')
  const invitingDependent = dependents?.find((d) => d.id === invitingId)

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!(e.target as HTMLFormElement).checkValidity()) { return }
    if (!invitingDependent) { return }
    try {
      await invite({
        email: inviteEmail,
        name: invitingDependent.full_name,
        dependentId: invitingDependent.id,
      })
      setInvitingId(null)
      setInviteEmail('')
      // reload so the now-linked spouse shows once (as a member, not a dependent)
      await selectDependents()
    } catch {
      // error surfaced via inviteState.error below; stay on the form
    }
  }

  // A spouse who has already been invited has their dependents row linked to a
  // user (user_id) and shows up as a member ("self") row instead. Hide such
  // linked rows from the dependents table to avoid listing them twice.
  // Guard: user_id may be undefined on rows from a pre-migration persisted state.
  const linkedUserIds = new Set((profiles ?? []).map((p) => p.id))
  const visibleDependents = dependents?.filter(
    (d) => !(d.user_id && linkedUserIds.has(d.user_id))
  )

  const [addDependentState, addDependent] = useAsyncAction(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(e.target as HTMLFormElement).checkValidity()) { return }
    if (!dependentRowComplete(toAdd)) {
      throw new Error('Dependent row is incomplete')
    }

    // `id` present means "restore the dependent that was just removed" -- the
    // remove button pops them back into this form, and the endpoint undeletes
    // rather than inserting a duplicate.
    await saveDependent(api, {
      id: toAdd.id,
      membershipId,
      fullName: toAdd.full_name,
      dateOfBirth: toAdd.date_of_birth,
      relationship: toAdd.relationship,
      authorizedRepresentativeName: toAdd.authorized_representative_name,
      authorizedRepresentativeRelationship: toAdd.authorized_representative_relationship,
      signatureData: toAdd.signature_data,
      ssnLast4: toAdd.ssn_last_4,
    })

    // reload dependents from server
    await selectDependents()
    setToAdd({})
  }, [api, toAdd, membershipId])
  if (addDependentState.error) { throw addDependentState.error }

  return <div className="dependents-form row">
    <div className="col-12 col-lg-6">
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: '40%' }}>Name</th>
            <th style={{ width: '30%' }}>
              <span className="d-none d-md-inline">Date of Birth</span>
              <span className="d-md-none">DOB</span>
            </th>
            <th style={{ width: '20%' }}>
              <span className="d-flex">
              Relationship
              <Tooltip placement="top"
                tooltip="The dependent's relationship to you, the Authorized Representative who signs on their behalf.">
              </Tooltip>
              </span>
            </th>
            <th style={{ width: '10%'}}></th>
          </tr>
        </thead>
        <tbody>
          {!dependents &&
            <div className="loading-bar"></div>}

          {profiles?.map((user) => {
            return <tr className="table-light" key={user.id}>
              <td>{user.full_name}</td>
              <td>{user.date_of_birth}</td>
              <td>self</td>
              <td></td>
            </tr>
          })}

          {visibleDependents?.map((dependent) => {
            const isRemoving = removing.includes(dependent.id)
            if (isDeleted(dependent)) {
              return null
            }

            const canInviteAsAdmin = dependent.relationship === 'spouse' && !dependent.user_id

            return <tr key={dependent.id}>
              <td>{dependent.full_name}</td>
              <td>{dependent.date_of_birth}</td>
              <td>{dependent.relationship}</td>
              <td>
                {canInviteAsAdmin &&
                  <button className="btn btn-link" title="Invite as admin"
                      onClick={() => {
                        if (!checkSubscription()) { return }
                        setInvitingId(dependent.id); setInviteEmail('')
                      }}>
                    <i className="material-icons">person_add</i>
                  </button>}
                <button className={`btn btn-link ${isRemoving && 'disabled'}`}
                    onClick={() => removeDependent(dependent.id)}>
                  {isRemoving ?
                    <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div> :
                    <i className="material-icons">close</i>
                  }
                </button>
              </td>
            </tr>
          })}
        </tbody>
      </table>

      {invitingDependent &&
        <form className="invite-spouse-panel border rounded p-3 mb-3" onSubmit={submitInvite}>
          <h5>Invite {invitingDependent.full_name} as a co-admin</h5>
          <p className="text-muted small">
            They'll get an email to sign in and will be able to manage this
            membership with you.
          </p>
          {inviteState.error &&
            <div className="alert alert-danger" role="alert">
              {isFamilyPlanRequiredError(inviteState.error)
                ? 'The Family plan is required to invite your spouse as a co-administrator. Please upgrade your plan and try again.'
                : "We couldn't send the invitation. Please check the email and try again."}
            </div>}
          <div className="input-group mb-2">
            <span className="input-group-text">Email</span>
            <input type="email" className="form-control" required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)} />
          </div>
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={inviteState.loading}>
              {inviteState.loading ?
                <span className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Sending...</span>
                </span> :
                'Send invitation'}
            </button>
            <button type="button" className="btn btn-link" disabled={inviteState.loading}
              onClick={() => { setInvitingId(null); setInviteEmail('') }}>
              Cancel
            </button>
          </div>
        </form>}
      <SubscriptionPromptModal />
    </div>

    <div className="col-12 col-lg-6">
      <h4>Add another dependent</h4>
    <form onSubmit={addDependent}>
      <div className="input-group">
        <span className='input-group-text'>Name</span>

        <input type="text" className="form-control" id="fullName"
                required
                value={toAdd.full_name || ''}
                onChange={(e) => { setToAdd({...toAdd, full_name: e.target.value }) }} />
      </div>

      <div className="input-group">
        <span className='input-group-text'>Date of Birth</span>
        <input name="datepic" placeholder="DateRange" type="date" id="dob"
              className="form-control"
              required
              value={toAdd.date_of_birth || ''}
              onChange={(e) => { setToAdd({...toAdd, date_of_birth: e.target.value }) }}></input>
      </div>

      <div className='input-group'>
        <span className='input-group-text'>Relationship</span>
        <DropdownSelect
          className="form-select"
          options={[
            { 'id': 'child', label: 'Child' },
            { 'id': 'spouse', label: 'Spouse' }
          ] as Array<{ id: DependentRelationship, label: string }>}
          renderOption={({id, label}) => label}
          value={toAdd.relationship}
          required
          onChange={(e) => {
            if (e.value && 'id' in e.value) {
              setToAdd({...toAdd, relationship: e.value.id})
            } else {
              setToAdd({...toAdd, relationship: undefined})
            }
          }}>
        </DropdownSelect>
      </div>
      <button type='submit'
          className={`btn btn-primary mt-2`}>
        {
          addDependentState.loading ?
            <span className="spinner-border" role="status">
              <span className="visually-hidden">Submitting...</span>
            </span> :
            `+ Add`
        }
      </button>
    </form>
    </div>
  </div>
}

function dependentRowComplete(
  row: Partial<DependentInsert>
): row is Partial<DependentInsert> & Pick<DependentInsert, 'full_name' | 'date_of_birth'> {
  return !!(
    row.full_name && row.date_of_birth
  )
}
